import { createStore } from "solid-js/store";

export type ReaderFontSize = "small" | "medium" | "large";

/**
 * Book grid ordering in navigation selector: canonical (Genesis ->
 * Revelation) or alphabetical. The published app had this toggle
 * (`OrderBooksByButton`) used to find a book whose position
 * in canon not remembered.
 */
type BookOrder = "canonical" | "alphabetical";

interface ReaderState {
  selectedVerses: Record<number, boolean>;
  fontSize: ReaderFontSize;
  showStrongs: boolean;
  bookOrder: BookOrder;
}

const PREFS_KEY = "bible-reader-prefs";

type ReaderPrefs = Pick<ReaderState, "fontSize" | "showStrongs" | "bookOrder">;

function loadPrefs(): ReaderPrefs {
  const defaults: ReaderPrefs = {
    fontSize: "medium",
    showStrongs: true,
    bookOrder: "canonical",
  };

  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) {
      return defaults;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return defaults;
    }
    const prefs = parsed as Partial<ReaderState>;
    return {
      fontSize:
        prefs.fontSize === "small" || prefs.fontSize === "medium" || prefs.fontSize === "large"
          ? prefs.fontSize
          : defaults.fontSize,
      showStrongs:
        typeof prefs.showStrongs === "boolean" ? prefs.showStrongs : defaults.showStrongs,
      bookOrder: prefs.bookOrder === "alphabetical" ? "alphabetical" : defaults.bookOrder,
    };
  } catch {
    return defaults;
  }
}

const [state, setState] = createStore<ReaderState>({
  selectedVerses: {},
  ...loadPrefs(),
});

function persistPrefs() {
  try {
    localStorage.setItem(
      PREFS_KEY,
      JSON.stringify({
        fontSize: state.fontSize,
        showStrongs: state.showStrongs,
        bookOrder: state.bookOrder,
      }),
    );
  } catch {
    // localStorage unavailable (private mode / quota): preferences stay in-memory only
  }
}

export const readerStore = {
  get selectedVerses() {
    return state.selectedVerses;
  },

  get fontSize() {
    return state.fontSize;
  },

  get showStrongs() {
    return state.showStrongs;
  },

  get bookOrder() {
    return state.bookOrder;
  },

  /** Toggles canonical <-> alphabetical in book grid (legacy parity). */
  toggleBookOrder() {
    setState("bookOrder", (order) => (order === "canonical" ? "alphabetical" : "canonical"));
    persistPrefs();
  },

  toggleVerse(verseNumber: number) {
    setState("selectedVerses", verseNumber, (selected) => !selected);
  },

  clearSelection() {
    setState({ selectedVerses: {} });
  },

  /**
   * Replaces selection with exact given set. Used by URL->store sync
   * (`?sel=`) to reproduce a shared selection without accumulating toggles.
   */
  setSelection(verseNumbers: number[]) {
    const next: Record<number, boolean> = {};
    for (const v of verseNumbers) {
      if (Number.isInteger(v) && v > 0) {
        next[v] = true;
      }
    }
    setState({ selectedVerses: next });
  },

  // Cycle small -> medium -> large -> small (parity with legacy FontSizeToggleButton)
  cycleFontSize() {
    const next: Record<ReaderFontSize, ReaderFontSize> = {
      small: "medium",
      medium: "large",
      large: "small",
    };
    setState("fontSize", (size) => next[size]);
    persistPrefs();
  },

  /** Sets font size (used by URL->store sync of `?fs=`). */
  setFontSize(size: ReaderFontSize) {
    setState("fontSize", size);
    persistPrefs();
  },

  toggleStrongs() {
    setState("showStrongs", (show) => !show);
    persistPrefs();
  },

  /** Sets Strong numbers visibility (used by URL->store sync of `?strongs=`). */
  setShowStrongs(show: boolean) {
    setState("showStrongs", show);
    persistPrefs();
  },

  getSelectedVersesArray(): number[] {
    return Object.entries(state.selectedVerses)
      .filter((entry) => entry[1])
      .map(([verse]) => Number.parseInt(verse, 10))
      .toSorted((a, b) => a - b);
  },
};
